import type { CustomerPreOrder } from "../../common/jsonRpcTypes";

/**
 * Checks whether a settings object (or its nested menus array) contains
 * at least one entry satisfying the given predicate.
 *
 * Supports three shapes:
 *  - `{ menus: [...] }` — iterates over `settings.menus`
 *  - `[...]`           — iterates over the array directly
 *  - `{ key: value }`  — iterates over `Object.values(settings)`
 *
 * @param settings - The preorder settings object to inspect
 * @param propertyChecker - Predicate applied to each entry
 * @returns true if any entry passes the predicate
 */
export function checkSettingsProperty(settings: any, propertyChecker: (entry: any) => boolean): boolean {
  if (!settings) return false;
  if (settings.menus && Array.isArray(settings.menus)) {
    return settings.menus.some((entry: any) => entry && propertyChecker(entry));
  }
  if (Array.isArray(settings)) {
    return settings.some((entry: any) => entry && propertyChecker(entry));
  }
  if (typeof settings === "object") {
    return Object.values(settings).some((entry: any) => entry && propertyChecker(entry));
  }
  return false;
}

/**
 * Determines whether a menu's payment is (or defaults to being) paid by a group member.
 *
 * A menu counts as group-member payment when payment is required and either the
 * `who_pays` value is explicitly `"group_member"` or it is not set at all. When a
 * menu requires payment but has no explicit `who_pays` value, the payment defaults
 * to being paid by a group member (and therefore not by the lead booker), regardless
 * of the `payment_required` type (full amount, percentage or fixed amount).
 *
 * @param menu - The menu payment settings (may be undefined)
 * @returns true when the menu requires group-member payment
 */
export function isGroupMemberPaymentMenu(menu: { who_pays?: string; payment_required?: string } | undefined): boolean {
  if (!menu?.payment_required) return false;
  // Default to group-member payment when who_pays is not explicitly set.
  return !menu.who_pays || menu.who_pays === "group_member";
}

/** The payment settings of a single pre-order menu. */
type MenuPaymentSettings = { menu_id?: string; who_pays?: string; payment_required?: string };

/**
 * Indexes a pre-order settings object's menus by `menu_id` so a basket item can
 * be resolved to the payment rules of the menu it came from.
 *
 * @param settings - The `customer_preorder_settings` object (may be undefined)
 * @returns Menu payment settings keyed by menu id
 */
function buildMenuPaymentMap(
  settings: { menus?: MenuPaymentSettings[] } | undefined,
): Record<string, MenuPaymentSettings> {
  const menus = Array.isArray(settings?.menus) ? settings.menus : [];
  const map: Record<string, MenuPaymentSettings> = {};
  for (const menu of menus) {
    if (menu?.menu_id) {
      map[menu.menu_id] = { who_pays: menu.who_pays, payment_required: menu.payment_required };
    }
  }
  return map;
}

/**
 * Returns true if any item in the basket belongs to a menu where the
 * payment is required and is set to be paid by a group member.
 *
 * When a menu requires payment but has no explicit `who_pays` value, the
 * payment defaults to being paid by a group member, regardless of the
 * `payment_required` type (full amount, percentage or fixed amount).
 *
 * @param customerPreOrder - The current customer pre-order (may be undefined)
 * @returns true when at least one basket item requires group-member payment
 */
export function isPaidByGroupMemberForBasket(customerPreOrder: CustomerPreOrder | undefined): boolean {
  if (!customerPreOrder?.items || !customerPreOrder?.customer_preorder_settings) return false;
  const menuPayMap = buildMenuPaymentMap(customerPreOrder.customer_preorder_settings);
  return (customerPreOrder.items ?? []).some((item) => isGroupMemberPaymentMenu(menuPayMap[item.menu_id]));
}

/**
 * Returns true when *any* menu the guest has ordered from is paid by a group
 * member — i.e. the guest placing this order pays for at least part of it.
 *
 * This is the condition for letting them adjust the discretionary service
 * charge: the charge is theirs to set when they contribute to it. An order drawn
 * only from pay-at-venue or lead-booker-paid menus is not charged to them, so
 * the rate stays as the venue configured it.
 *
 * Differs from {@link isPaidByGroupMemberForBasket} by ignoring zero-quantity
 * items, so an item added and then removed does not keep the charge adjustable.
 *
 * @param customerPreOrder - The current customer pre-order (may be undefined)
 * @returns true when at least one positive-quantity item is group-member paid
 */
export function isAnyOrderedMenuPaidByGroupMember(customerPreOrder: CustomerPreOrder | undefined): boolean {
  if (!customerPreOrder?.items || !customerPreOrder?.customer_preorder_settings) return false;
  const menuPayMap = buildMenuPaymentMap(customerPreOrder.customer_preorder_settings);
  return customerPreOrder.items
    .filter((item) => (item.quantity ?? 0) > 0)
    .some((item) => isGroupMemberPaymentMenu(menuPayMap[item.menu_id]));
}
